<script setup lang="ts">
import type { DayOffExclusionOptions } from '@/composables/useDayOff';
import { usePlatform } from '@/composables/usePlatform';
import { getRangeWithExclusions } from '@/utils/date';
import { useWindowSize } from '@vueuse/core';
import Button from 'primevue/button';
import Calendar from 'primevue/calendar';
import Sidebar from 'primevue/sidebar';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { width } = useWindowSize();
const isMobilePhone = usePlatform().isMobilePhone;

const props = defineProps<{
  modelValue: boolean;
  exclusionOptions?: DayOffExclusionOptions;
  disabledDates?: Date[];
  disabledDaysOfWeek?: number[];
  remainingDaysOff?: number;
  shouldShowLimitInfo?: boolean;
  loading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'add-days-off', dates: Date[]): void;
}>();

const inputDates = ref<Date[] | undefined>();

const sideBarPosition = computed(() => (width.value >= 768 ? 'right' : 'bottom'));

const selectedDateRange = computed(() => {
  if (!inputDates.value || inputDates.value.length === 0) return null;

  // Normalize dates to remove time components and ensure consistent timezone handling
  const normalizeDate = (date: Date) => {
    // Create a new date in local timezone, ensuring we don't have timezone shift issues
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const normalized = new Date(year, month, day, 0, 0, 0, 0);
    return normalized;
  };

  // Fix: Change assignment (=) to comparison (===)
  if (inputDates.value.length === 1 || !inputDates.value[1]) {
    const date = normalizeDate(inputDates.value[0]);
    return { start: date, end: date };
  } else {
    return {
      start: normalizeDate(inputDates.value[0]),
      end: normalizeDate(inputDates.value[1]),
    };
  }
});

const numberOfDaysToTake = computed(() => {
  if (!selectedDateRange.value) return 0;

  const daysInRange = getRangeWithExclusions(selectedDateRange.value.start, selectedDateRange.value.end, props.exclusionOptions || {});

  // Filter out disabled dates
  const validDays = daysInRange.filter((date) => {
    return !props.disabledDates?.some((disabled) => disabled.toDateString() === date.toDateString());
  });

  return validDays.length;
});

const isValidRequest = computed(() => {
  if (!selectedDateRange.value || numberOfDaysToTake.value <= 0) return false;

  // If limit info is hidden, always allow adding days off
  if (!props.shouldShowLimitInfo) {
    return numberOfDaysToTake.value > 0;
  }

  // Check against remaining days
  return (props.remainingDaysOff || 0) >= numberOfDaysToTake.value;
});

const addButtonLabel = computed(() => {
  let label = t('dayOff.add');
  if (numberOfDaysToTake.value === 1) {
    label += ` ${t('dayOff.theDayOff')}`;
  } else if (numberOfDaysToTake.value > 1) {
    label += ` ${t('dayOff.theDaysOff', { count: numberOfDaysToTake.value })}`;
  }
  return label;
});

function handleAddDaysOff() {
  if (!selectedDateRange.value || !isValidRequest.value) return;

  const daysToAdd = getRangeWithExclusions(selectedDateRange.value.start, selectedDateRange.value.end, props.exclusionOptions || {}).filter(
    (date) => {
      return !props.disabledDates?.some((disabled) => disabled.toDateString() === date.toDateString());
    },
  );

  emit('add-days-off', daysToAdd);
  inputDates.value = undefined;
}

function handleClose() {
  inputDates.value = undefined;
  emit('update:modelValue', false);
}

// Watch for external visibility changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) {
      inputDates.value = undefined;
    }
  },
);

// Watch input date and log them with detailed information
// watch(inputDates, (newDates) => {
//   console.log('Selected dates changed:', newDates);
//   if (newDates && newDates.length > 0) {
//     console.log('Date details:', {
//       firstDate: {
//         value: newDates[0],
//         toString: newDates[0]?.toString(),
//         toISOString: newDates[0]?.toISOString(),
//         timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
//       },
//       secondDate: newDates[1] ? {
//         value: newDates[1],
//         toString: newDates[1]?.toString(),
//         toISOString: newDates[1]?.toISOString()
//       } : null
//     });
//   }
// });

// Add debugging for the computed properties
// watch(selectedDateRange, (newRange) => {
//   if (newRange) {
//     console.log('Selected date range:', {
//       start: newRange.start.toISOString(),
//       end: newRange.end.toISOString(),
//       startLocal: newRange.start.toString(),
//       endLocal: newRange.end.toString()
//     });
//   }
// });

// watch(numberOfDaysToTake, (newCount) => {
// console.log('Number of days to take:', newCount);
// if (selectedDateRange.value) {
//   const daysInRange = getRangeWithExclusions(
//     selectedDateRange.value.start,
//     selectedDateRange.value.end,
//     props.exclusionOptions || {}
//   );
// console.log('Days in range before filtering:', daysInRange.map(d => d.toISOString()));

// const validDays = daysInRange.filter((date) => {
//   return !props.disabledDates?.some((disabled) => disabled.toDateString() === date.toDateString());
// });
// console.log('Valid days after filtering:', validDays.map(d => d.toISOString()));
// }
// });
</script>

<template>
  <Sidebar
    :visible="modelValue"
    :header="t('dayOff.newDayOffSidebarTitle')"
    block-scroll
    :position="sideBarPosition"
    style="padding-bottom: calc(0.75rem + var(--safe-area-inset-bottom, 0px))"
    class="h-auto sm:max-w-md md:h-full md:w-full"
    @update:visible="emit('update:modelValue', $event)"
    @hide="handleClose"
  >
    <div class="flex h-full flex-col gap-2">
      <!-- Available days info -->
      <p v-if="shouldShowLimitInfo" class="flex flex-row justify-between">
        {{ t('dayOff.availableDays') }}:
        <span class="font-bold">{{ remainingDaysOff }}</span>
      </p>

      <!-- Selected days info -->
      <p class="flex flex-row justify-between">
        {{ t('dayOff.selectedDays') }}:
        <span class="font-bold">{{ numberOfDaysToTake }}</span>
      </p>

      <!-- Date picker -->
      <h4 class="mb-3 mt-4">{{ t('dayOff.chooseDate') }}</h4>
      <Calendar
        v-model="inputDates"
        selection-mode="range"
        :min-date="new Date()"
        class="w-full"
        select-other-months
        inline
        show-button-bar
        :disabled-dates="disabledDates"
        :disabled-days="disabledDaysOfWeek"
        :touch-u-i="isMobilePhone"
      />

      <!-- Add button -->
      <div class="flex-grow-1 mt-4 flex flex-col justify-end">
        <Button icon="pi pi-plus" class="w-full" :loading="loading" :label="addButtonLabel" :disabled="!isValidRequest" @click="handleAddDaysOff" />
      </div>
    </div>
  </Sidebar>
</template>

<style scoped>
:deep(.p-datepicker table td > span) {
  width: 2.5rem;
  height: 2.5rem;
}

/* Enhanced selected date visibility using CSS custom properties */
:deep(.p-datepicker table td span[data-p-selected='true']) {
  background-color: rgb(73 120 148) !important;
  /* Using the primary-500 color directly */
  color: white !important;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  border: 2px solid rgb(73 120 148);
}

/* Range selection styling for table cells */
:deep(.p-datepicker table td[data-p-selected='true']) {
  background-color: rgba(73, 120, 148, 0.15) !important;
}

/* Today + selected styling */
:deep(.p-datepicker table td span[data-p-selected='true'][data-p-today='true']) {
  background-color: rgb(59 107 134) !important;
  /* Slightly darker for today */
  color: white !important;
  font-weight: 700;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
  border: 2px solid rgb(59 107 134);
}

/* Hover states for better UX */
:deep(.p-datepicker table td span:hover:not([data-p-disabled='true']):not([data-p-selected='true'])) {
  background-color: rgba(73, 120, 148, 0.1) !important;
  border: 1px solid rgba(73, 120, 148, 0.3);
}

/* Disabled dates styling */
:deep(.p-datepicker table td span[data-p-disabled='true']) {
  opacity: 0.4;
  background-color: transparent !important;
  color: #9ca3af !important;
}

:deep(.p-sidebar .p-sidebar-content) {
  padding-bottom: 1rem !important;
}
</style>
